/**
 * ItemReturnModal - Modal for processing item returns with overage calculations
 */

import React, { useState, useEffect, memo, useCallback } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ItemReturnModal = ({ isOpen, reservation, onClose, onSubmit, taxRate }) => {

  const [returnData, setReturnData] = useState({
    actualReturnTime: '',
    notes: '',
    refundAmount: '0',
    refundReason: ''
  });

  const [isRefundSectionExpanded, setIsRefundSectionExpanded] = useState(false);
  const [calculations, setCalculations] = useState({
    expectedDuration: 0,
    actualDuration: 0,
    overageHours: 0,
    overageAmount: 0,
    baseAmount: 0,
    subtotal: 0,
    taxAmount: 0,
    refundAmount: 0,
    finalTotal: 0
  });

  // Payment split state for overage
  const [overagePayment, setOveragePayment] = useState({
    cashAmount: '0',
    creditAmount: 0,
    surchargeAmount: 0,
    chargeOnCard: 0,
    totalToCollect: 0
  });

  const [surchargePercentage, setSurchargePercentage] = useState(0);

  // Fetch surcharge percentage from financial settings
  useEffect(() => {
    const fetchSurcharge = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/settings/financial/payment', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data?.data?.surcharge) {
          setSurchargePercentage(response.data.data.surcharge.percentage || 0);
        }
      } catch (error) {
        console.error('Error fetching surcharge settings:', error);
        setSurchargePercentage(0);
      }
    };

    if (isOpen) {
      fetchSurcharge();
    }
  }, [isOpen]);

  // Initialize return time to current time when modal opens
  useEffect(() => {
    if (isOpen && reservation) {
      setReturnData({
        actualReturnTime: formatDateTimeForInput(new Date()),
        notes: '',
        refundAmount: '0',
        refundReason: ''
      });
      setOveragePayment({
        cashAmount: '0',
        creditAmount: 0,
        surchargeAmount: 0,
        chargeOnCard: 0,
        totalToCollect: 0
      });
    }
  }, [isOpen, reservation]);

  // Recalculate amounts when return time or refund changes
  useEffect(() => {
    if (reservation && returnData.actualReturnTime) {
      calculateAmounts();
    }
  }, [returnData.actualReturnTime, returnData.refundAmount, reservation]);

  // Calculate overage payment split when cash amount or overage changes
  useEffect(() => {
    if (calculations.overageAmount > 0) {
      calculateOveragePaymentSplit();
    }
  }, [overagePayment.cashAmount, calculations.overageAmount, surchargePercentage]);

  // Format datetime for input field
  const formatDateTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Calculate overage payment split
  const calculateOveragePaymentSplit = () => {
    if (calculations.overageAmount <= 0) return;

    const cashAmount = parseFloat(overagePayment.cashAmount) || 0;
    const overageAmount = calculations.overageAmount;

    // Ensure cash doesn't exceed overage
    const actualCash = Math.min(cashAmount, overageAmount);
    const creditAmount = Math.max(0, overageAmount - actualCash);

    // Calculate and round surcharge to 2 decimal places (standard for currency)
    const surchargeAmount = Math.round((creditAmount * surchargePercentage) * 100) / 100;
    const chargeOnCard = Math.round((creditAmount + surchargeAmount) * 100) / 100;
    const totalToCollect = Math.round((actualCash + chargeOnCard) * 100) / 100;

    setOveragePayment(prev => ({
      ...prev,
      creditAmount,
      surchargeAmount,
      chargeOnCard,
      totalToCollect
    }));
  };

  // Calculate overage and total amounts
  const calculateAmounts = () => {
    if (!reservation || !returnData.actualReturnTime) return;

    const startTime = new Date(reservation.startTime);
    const expectedEndTime = new Date(reservation.endTime || 
      new Date(startTime.getTime() + (reservation.reservationDuration || 0) * 60 * 60 * 1000));
    const actualReturnTime = new Date(returnData.actualReturnTime);

    // Calculate durations in hours
    const expectedDuration = parseFloat(reservation.reservationDuration) || 0;
    const actualDurationMs = actualReturnTime - startTime;
    const actualDurationRaw = Math.max(0, actualDurationMs / (1000 * 60 * 60));

    // Round actual duration to 2 decimal places
    const actualDuration = Math.round(actualDurationRaw * 100) / 100;

    // Calculate overage and round to 2 decimal places
    const overageHoursRaw = Math.max(0, actualDuration - expectedDuration);
    const overageHours = Math.round(overageHoursRaw * 100) / 100;

    // Get hourly rate (try multiple sources)
    const hourlyRate =
      reservation.hourlyRateAtBooking ||
      reservation.inventoryItem?.hourlyRate ||
      reservation.inventoryItem?.productType?.defaultHourlyRate ||
      20; // Default fallback

    // Calculate amounts using rounded overage hours
    const baseAmount = expectedDuration * hourlyRate;
    const overageAmount = Math.round((overageHours * hourlyRate) * 100) / 100;
    const refundAmount = parseFloat(returnData.refundAmount) || 0;

    const subtotal = baseAmount + overageAmount - refundAmount;
    const taxAmount = Math.round((subtotal * (taxRate / 100)) * 100) / 100;
    const finalTotal = Math.round((subtotal + taxAmount) * 100) / 100;

    console.log('🧮 Overage Calculation Details:', {
      actualDurationRaw,
      actualDurationRounded: actualDuration,
      overageHoursRaw,
      overageHoursRounded: overageHours,
      hourlyRate,
      overageAmount,
      calculation: `${overageHours} × ${hourlyRate} = ${overageAmount}`
    });

    setCalculations({
      expectedDuration,
      actualDuration,
      overageHours,
      overageAmount,
      baseAmount,
      subtotal,
      taxAmount,
      refundAmount,
      finalTotal,
      hourlyRate
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert the datetime-local value to ISO string with timezone info
    const actualReturnTimeISO = new Date(returnData.actualReturnTime).toISOString();

    // Prepare submission data
    const submissionData = {
      status: 'COMPLETED',
      actualReturnTime: actualReturnTimeISO, // Send ISO string with timezone info
      notes: returnData.notes,
      overageHours: calculations.overageHours,
      overageAmount: calculations.overageAmount,
      refundAmount: parseFloat(returnData.refundAmount) || 0,
      refundReason: returnData.refundReason || null,
      totalAmount: calculations.finalTotal,
      processedByEmployeeId: 'current-user', // Should be actual user ID
      // Include payment split data for overage
      overagePaymentSplit: calculations.overageAmount > 0 ? {
        cashAmount: parseFloat(overagePayment.cashAmount) || 0,
        creditAmount: overagePayment.creditAmount,
        surchargeAmount: overagePayment.surchargeAmount,
        surchargePercentage: surchargePercentage
      } : null
    };

    onSubmit(submissionData);
  };

  if (!isOpen || !reservation) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Process Return
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Reservation Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Reservation Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Customer:</span>
                      <div className="text-gray-900 font-medium">
                        {reservation.customer ? 
                          `${reservation.customer.firstName} ${reservation.customer.lastName}` : 
                          'Unknown'
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Item:</span>
                      <div className="text-gray-900 font-medium">
                        {reservation.inventoryItem ? 
                          `${reservation.inventoryItem.productType?.name} #${reservation.inventoryItem.itemNumber}` : 
                          'Unknown'
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Start Time:</span>
                      <div className="text-gray-900 font-medium">{formatDate(reservation.startTime)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Expected Return:</span>
                      <div className="text-gray-900 font-medium">{formatDate(reservation.endTime)}</div>
                    </div>
                  </div>
                </div>

                {/* Return Time Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Return Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={returnData.actualReturnTime}
                    onChange={(e) => setReturnData({ ...returnData, actualReturnTime: e.target.value })}
                    min={formatDateTimeForInput(new Date(new Date(reservation.startTime).getTime() + 5 * 60 * 1000))}
                    max={formatDateTimeForInput(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Return time must be at least 5 minutes after start time
                  </p>
                </div>

                {/* Overage Calculation */}
                {calculations.overageHours > 0 && (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-medium text-yellow-900 mb-2">Overage Charges</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-yellow-700">Expected Duration:</span>
                          <span className="font-medium">{calculations.expectedDuration.toFixed(2)} hours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-yellow-700">Actual Duration:</span>
                          <span className="font-medium">{calculations.actualDuration.toFixed(2)} hours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-yellow-700">Overage Hours:</span>
                          <span className="font-medium text-yellow-900">{calculations.overageHours.toFixed(2)} hours</span>
                        </div>
                        <div className="flex justify-between border-t border-yellow-200 pt-2">
                          <span className="text-yellow-700">Overage Amount:</span>
                          <span className="font-semibold text-yellow-900">
                            ${calculations.overageAmount.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-xs text-yellow-600">
                          ({calculations.overageHours.toFixed(2)} hours × ${calculations.hourlyRate?.toFixed(2)}/hr)
                        </div>
                      </div>
                    </div>

                    {/* Overage Payment Split Calculator */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-3">Overage Payment Split</h4>
                      <div className="space-y-3">
                        {/* Cash Amount Input */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cash Amount
                          </label>
                          <input
                            type="number"
                            value={overagePayment.cashAmount}
                            onChange={(e) => setOveragePayment({ ...overagePayment, cashAmount: e.target.value })}
                            min="0"
                            max={calculations.overageAmount}
                            step="0.01"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter cash amount..."
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Credit amount is auto-calculated. Surcharge applies to credit card payments.
                          </p>
                        </div>

                        {/* Payment Breakdown */}
                        <div className="bg-white rounded-lg p-3 space-y-2 text-sm border border-blue-200">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Credit Amount:</span>
                            <span className="font-medium text-gray-900">${overagePayment.creditAmount.toFixed(2)}</span>
                          </div>
                          {surchargePercentage > 0 && overagePayment.creditAmount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-700">Surcharge ({(surchargePercentage * 100).toFixed(2)}%):</span>
                              <span className="font-medium text-gray-900">${overagePayment.surchargeAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-blue-200 pt-2 font-semibold text-blue-900">
                            <span>Charge on Card:</span>
                            <span className="text-lg">${overagePayment.chargeOnCard.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Total to Collect */}
                        <div className="bg-white rounded-lg p-3 border-2 border-blue-300">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-semibold text-gray-900">Total to Collect:</span>
                            <span className="text-2xl font-bold text-blue-900">${overagePayment.totalToCollect.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={returnData.notes}
                    onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Any additional notes about the return..."
                  />
                </div>

                {/* Refund Section - Collapsible */}
                <div className="border border-gray-200 rounded-lg">
                  <div 
                    className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-50"
                    onClick={() => setIsRefundSectionExpanded(!isRefundSectionExpanded)}
                  >
                    <h4 className="font-medium text-gray-900">Refund (Optional)</h4>
                    {isRefundSectionExpanded ? 
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" /> : 
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    }
                  </div>
                  
                  {isRefundSectionExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Refund Amount
                        </label>
                        <input
                          type="number"
                          value={returnData.refundAmount}
                          onChange={(e) => setReturnData({ ...returnData, refundAmount: e.target.value })}
                          min="0"
                          step="0.01"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Refund Reason
                        </label>
                        <input
                          type="text"
                          value={returnData.refundReason}
                          onChange={(e) => setReturnData({ ...returnData, refundReason: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Reason for refund..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Final Calculation */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-3">Final Calculation</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Amount:</span>
                      <span className="font-medium">${calculations.baseAmount.toFixed(2)}</span>
                    </div>
                    {calculations.overageAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Overage:</span>
                        <span className="font-medium text-yellow-700">+${calculations.overageAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {calculations.refundAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Refund:</span>
                        <span className="font-medium text-red-600">-${calculations.refundAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-green-200 pt-2">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${calculations.subtotal.toFixed(2)}</span>
                    </div>
                    {taxRate > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax ({taxRate}%):</span>
                        <span className="font-medium">${calculations.taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-green-200 pt-2">
                      <span className="text-green-900 font-semibold">Final Total:</span>
                      <span className="text-green-900 font-semibold text-lg">${calculations.finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Process Return
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default memo(ItemReturnModal);